import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

const STORAGE_KEY = 'scroll-positions'
const MAX_ENTRIES = 50
const RESTORE_TIMEOUT = 1500
const SCROLL_THROTTLE = 150

function getSavedPositions(): Record<string, number> {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}')
  } catch {
    return {}
  }
}

function savePosition(key: string, y: number) {
  const positions = getSavedPositions()
  positions[key] = y

  // Cap entries by removing oldest
  const keys = Object.keys(positions)
  if (keys.length > MAX_ENTRIES) {
    for (const k of keys.slice(0, keys.length - MAX_ENTRIES)) {
      delete positions[k]
    }
  }

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions))
}

export function useScrollRestoration() {
  const { key } = useLocation()
  const prevKeyRef = useRef(key)

  useEffect(() => {
    window.history.scrollRestoration = 'manual'
  }, [])

  // Continuously save scroll position so it's always current before navigation
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null

    const onScroll = () => {
      if (timer) return
      timer = setTimeout(() => {
        timer = null
        savePosition(key, window.scrollY)
      }, SCROLL_THROTTLE)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('pagehide', () => savePosition(key, window.scrollY))

    return () => {
      window.removeEventListener('scroll', onScroll)
      if (timer) clearTimeout(timer)
    }
  }, [key])

  // Restore scroll position before paint to avoid flash-to-top
  useLayoutEffect(() => {
    const prevKey = prevKeyRef.current
    prevKeyRef.current = key

    if (prevKey === key) return // Initial mount

    const saved = getSavedPositions()[key]

    if (saved != null && saved > 0) {
      // Try immediate restore (works if content is already cached/rendered)
      window.scrollTo(0, saved)

      // Watch for page growth via ResizeObserver instead of polling
      const docEl = document.documentElement
      if (docEl.scrollHeight < saved + window.innerHeight) {
        const observer = new ResizeObserver(() => {
          if (docEl.scrollHeight >= saved + window.innerHeight) {
            window.scrollTo(0, saved)
            observer.disconnect()
            clearTimeout(timeout)
          }
        })
        observer.observe(docEl)

        // Give up if content never grows tall enough
        const timeout = setTimeout(() => {
          window.scrollTo(0, saved)
          observer.disconnect()
        }, RESTORE_TIMEOUT)

        return () => {
          observer.disconnect()
          clearTimeout(timeout)
        }
      }
    } else {
      // New navigation — scroll to top
      window.scrollTo(0, 0)
    }
  }, [key])
}
